<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'

/**
 * Defers both the render *and* the JS chunk of a below-the-fold section until
 * it approaches the viewport. Pair with `defineAsyncComponent` in the parent:
 * the dynamic import only fires once this wrapper flips `visible`, so the
 * section's code never enters the initial bundle.
 *
 * `minHeight` reserves space so flipping in does not shift the page.
 */
const props = defineProps({
  minHeight: { type: String, default: '480px' },
  /** How far ahead of the viewport to start loading. */
  rootMargin: { type: String, default: '600px 0px' }
})

// No IntersectionObserver (SSR, very old browsers) means render immediately —
// content must never be gated behind a capability the client might not have.
const visible = ref(typeof IntersectionObserver === 'undefined')
const anchor = ref(null)

let observer = null

onMounted(() => {
  if (visible.value || !anchor.value) return

  observer = new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        visible.value = true
        observer?.disconnect()
        observer = null
      }
    },
    { rootMargin: props.rootMargin }
  )

  observer.observe(anchor.value)
})

onBeforeUnmount(() => {
  observer?.disconnect()
  observer = null
})
</script>

<template>
  <!--
    Once mounted, `defer-paint` (content-visibility: auto) lets the browser skip
    style, layout, and paint for this subtree whenever it scrolls out of view.
    `contain-intrinsic-size` is seeded from the same minHeight, so the scroll
    height stays stable and nothing shifts.
  -->
  <div
    ref="anchor"
    :class="visible ? 'defer-paint' : undefined"
    :style="visible ? { containIntrinsicSize: `auto ${minHeight}` } : { minHeight }"
  >
    <slot v-if="visible" />
  </div>
</template>
